import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationCircleIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { friendlyError } from '../utils/friendlyError';

export default function PasswordResetModal({ show, onClose, auth }) {
    const [resetEmail, setResetEmail] = useState("");
    const [resetNotice, setResetNotice] = useState("");
    const [isSuccess, setIsSuccess] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // Clear states when modal is opened
    useEffect(() => {
        if (show) {
            // Check local storage for cooldown
            const cooldownEnd = localStorage.getItem('passwordResetCooldown');
            if (cooldownEnd && Date.now() < parseInt(cooldownEnd)) {
                setIsButtonDisabled(true);
                setResetNotice("Please wait before requesting another reset email.");
                setIsSuccess(false);
            } else {
                setIsButtonDisabled(false);
                setResetNotice("");
                setIsSuccess(false);
            }
        }
    }, [show]);

    const handlePasswordReset = async () => {
        if (!resetEmail) {
            setResetNotice("Please enter your email address to reset your password.");
            setIsSuccess(false);
            return;
        }

        try {
            setIsButtonDisabled(true);
            const functions = getFunctions(undefined, 'europe-west1');
            const sendCustomEmail = httpsCallable(functions, 'sendPasswordResetEmail');
            
            await sendCustomEmail({
                email: resetEmail
            });

            // Set cooldown in localStorage (2 minutes from now)
            localStorage.setItem('passwordResetCooldown', (Date.now() + 120000).toString());

            setResetNotice("Password reset email sent. Please check your inbox, it may end in your junk folder.");
            setIsSuccess(true);
            setIsButtonDisabled(true);

        } catch (error) {
            console.error("Detailed error:", error);
            setResetNotice(friendlyError(error, 'Error sending password reset email. Please try again.'));
            setIsSuccess(false);
            setIsButtonDisabled(false);
        }
    };

    return (
        <Transition appear show={show} as={React.Fragment}>
            <Dialog as="div" className="relative z-10" onClose={onClose}>
                <Transition.Child
                    as={React.Fragment}
                    enter="transition-opacity duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={React.Fragment}
                            enter="transition-all duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="transition-all duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative gch-card overflow-hidden px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                <div className="flex justify-between items-center">
                                    <Dialog.Title as="h3" className="text-base font-semibold gch-heading-sm">
                                        Password reset
                                    </Dialog.Title>
                                    <button
                                        type="button"
                                        className="p-1 rounded-md gch-text-muted hover:gch-text-primary focus:outline-none focus:ring-2 focus:ring-[var(--admin-accent)]"
                                        onClick={onClose}
                                    >
                                        <XCircleIcon className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                </div>
                                <p className="mt-2 mb-4 text-sm gch-text-secondary">
                                    Enter your email and we’ll send a reset link. It may take up to 5 minutes to arrive.
                                </p>
                                <input
                                    type="email"
                                    placeholder="Email address"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    className="gch-input-ghost block w-full rounded-[var(--admin-card-radius)] px-4 py-3 gch-text-primary focus:ring-2 focus:ring-[var(--admin-accent)] text-sm placeholder:opacity-40 placeholder:gch-text-muted"
                                />
                                <div className="mt-5">
                                    {resetNotice && (
                                        <div className={`text-sm mb-4 ${isSuccess ? 'text-green-400' : 'text-red-400'}`}>
                                            <div className="flex items-start gap-2">
                                                {isSuccess ? (
                                                    <CheckCircleIcon className="h-5 w-5 shrink-0 mt-0.5" />
                                                ) : (
                                                    <ExclamationCircleIcon className="h-5 w-5 shrink-0 mt-0.5" />
                                                )}
                                                <span>{resetNotice}</span>
                                            </div>
                                        </div>
                                    )}
                                    {!isButtonDisabled ? (
                                        <button
                                            type="button"
                                            onClick={handlePasswordReset}
                                            className="w-full inline-flex items-center justify-center rounded-[var(--admin-card-radius)] px-4 py-2.5 text-sm font-semibold gch-text-primary border border-[var(--admin-border)] hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[var(--admin-accent)] transition-colors"
                                            style={{ background: 'rgba(255,255,255,0.06)' }}
                                        >
                                            Send reset email
                                        </button>
                                    ) : null}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}